<template>
  <v-container class="h-full">
    <h1 class="mb-2 flex gap-4 items-center">
      <span v-if="!localGroup">
        Loading Group...
      </span>
      <template v-else-if="groupId">
        <span>Edit "{{ localGroup?.name ?? 'Untitled' }}" Group</span>
        <v-menu
          offset="8"
          :disabled="loading || groupsLoading"
        >
          <template #activator="{ props }">
            <div class="d-flex justify-center text-base">
              <v-btn
                v-bind="props"
                size="32"
                color="primary"
                icon="mdi-dots-horizontal"
              />
            </div>
          </template>
          <v-list>
            <v-list-item>
              <v-list-item-title class="!text-sm cursor-pointer" @click="openAddEnvironmentDialog">
                Add Group to Environment...
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>
      <span v-else>
        Create New Group
      </span>
    </h1>

    <div class="max-w-[1200px]">
      <v-card color="darknavyblue" elevation="5" class="pa-4 mb-4 overflow-visible">
        <v-card-title>Group Details</v-card-title>
        <v-card-text>
          <v-progress-circular v-if="!localGroup" indeterminate />
          <template v-else-if="!groupId">
            <v-text-field
              v-model="localGroup.name"
              label="Group Name"
              :disabled="loading"
              clearable
            />
          </template>
          <div v-else class="text-base">
            <div class="flex flex-col md:flex-row gap-4">
              <div class="md:w-1/2">
                <div class="mb-2">
                  <p><strong>Group ID</strong></p>
                  <p class="opacity-50" v-text="localGroup.id" />
                </div>
                <div class="mb-2">
                  <p><strong>Group Name</strong></p>
                  <v-text-field
                    v-model="localGroup.name"
                    :disabled="loading"
                    clearable
                  />
                </div>
              </div>
              <div class="md:w-1/2">
                <div class="mb-2">
                  <p><strong>Metadata</strong></p>
                  <GroupMetadataField ref="metadataField" v-model="localGroup.metadata" />
                </div>
              </div>
            </div>
            <v-btn
              block
              color="primary"
              class="mt-4"
              :loading="loading"
              :disabled="actionDisabled"
              @click="updateGroup()"
            >
              Update Details
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <v-card color="darknavyblue" elevation="5" class="pa-4 mb-4">
        <v-card-title>Group Programs</v-card-title>
        <div class="flex flex-col md:flex-row align-start">
          <v-card-text class="w-full md:w-1/2">
            <h3 class="mb-4">
              Add Program(s)
            </h3>
            <v-progress-circular v-if="programsLoading || !localGroup" indeterminate />
            <div v-else-if="!programItems.length" class="p-4 text-center opacity-50">
              <template v-if="scopedPrograms.length && groupPrograms.length >= scopedPrograms.length">
                This group already has access to all available programs.
              </template>
              <template v-else>
                No programs enrolled in the current Environment + Domain.
              </template>
            </div>
            <template v-else>
              <v-select
                v-model="selectedPrograms"
                label="Select Programs to Add"
                :items="programItems"
                :disabled="loading"
                item-value="id"
                item-title="name"
                return-object
                multiple
                clearable
              >
                <template #item="{ internalItem: item, props }">
                  <v-list-item v-bind="props" :title="item.raw.name" :subtitle="item.raw.client" />
                </template>
              </v-select>
              <v-select
                v-model="selectedRelationship"
                label="Select Program Relationship"
                :items="programRelationships"
                :disabled="loading"
                item-title="name"
                item-value="type"
                clearable
              />
              <v-checkbox
                v-model="addGroupUsers"
                label="Also grant all Group Users access to this Program"
                density="compact"
                :disabled="loading || grantGroupUsersUnavailable"
              />
              <v-select
                v-if="addGroupUsers"
                v-model="selectedGroupUserRole"
                label="Select Role for Group Users"
                :items="availableGroupUserRoles"
                :disabled="loading"
                item-title="name"
                item-value="role"
                clearable
                no-data-text="No roles available for the current Domain"
              />
              <v-btn
                block
                color="primary"
                :disabled="!selectedPrograms.length || !selectedRelationship || (addGroupUsers && !selectedGroupUserRole) || loading"
                @click="addPrograms()"
              >
                Add Program Access
              </v-btn>
            </template>
          </v-card-text>
          <v-card-text class="w-full md:w-1/2">
            <h3 class="mb-4">
              Program Access
            </h3>
            <v-list bg-color="transparent" class="py-0">
              <v-list-item
                v-for="relationship of groupPrograms"
                :key="relationship.program?.id ?? relationship.program?.name"
                :title="relationship.program?.name"
                :subtitle="relationship.program?.client"
                variant="tonal"
                class="!px-4 !py-4 mb-1 rounded"
                border
              >
                <template #append>
                  <v-chip
                    variant="flat"
                    density="compact"
                    color="success"
                    v-text="prettyGroupRelationshipTypes.get(relationship.relationshipType)"
                  />
                  <v-btn
                    color="error"
                    icon="mdi-minus"
                    size="24"
                    class="ml-2"
                    :disabled="loading"
                    :loading="loading"
                    @click="removeProgram(relationship.program)"
                  />
                </template>
              </v-list-item>
              <v-list-item
                v-if="!loading && localGroup && !groupPrograms.length"
                class="text-center opacity-50"
              >
                No programs associated with this group.
              </v-list-item>
            </v-list>
          </v-card-text>
        </div>
      </v-card>

      <v-card
        v-if="groupId"
        color="darknavyblue"
        elevation="5"
        class="pa-4 mb-4"
      >
        <v-card-title>Group Users</v-card-title>
        <v-card-text>
          <v-progress-circular v-if="!localGroup" indeterminate />
          <div v-else>
            <v-list bg-color="transparent" class="py-0">
              <v-list-item
                v-for="(user, index) of groupUsers"
                :key="user.user?.id ?? index"
                :title="`${user.user?.firstName} ${user.user?.lastName}`"
                :subtitle="user.user?.email"
                variant="tonal"
                class="!px-4 !py-4 mb-1 rounded"
                border
              >
                <template #append>
                  <v-icon
                    v-if="user.user"
                    icon="mdi-pencil"
                    size="24"
                    class="ml-2"
                    :disabled="loading"
                    :loading="loading"
                    @click="viewUser(user.user.id)"
                  />
                </template>
              </v-list-item>
              <v-list-item
                v-if="!loading && localGroup && !groupUsers.length"
                class="text-center opacity-50"
              >
                No users found in this group.
              </v-list-item>
            </v-list>
          </div>
        </v-card-text>
      </v-card>

      <v-card
        v-if="!groupId"
        key="v-card-2"
        color="darknavyblue"
        elevation="5"
        class="pa-4 mb-4"
      >
        <v-card-text>
          <v-btn
            block
            color="primary"
            :loading="loading"
            :disabled="actionDisabled"
            @click="createGroup()"
          >
            Create Group
          </v-btn>
        </v-card-text>
      </v-card>
    </div>
    <v-dialog
      v-model="addEnvironmentDialog"
      max-width="720"
      :persistent="addEnvironmentLoading"
    >
      <v-card class="pa-4">
        <v-card-title>
          <div class="flex gap-4 items-center justify-between">
            <span>Add Group to Environment</span>
            <v-btn
              dense
              icon="mdi-close"
              variant="plain"
              :disabled="addEnvironmentLoading"
              @click="addEnvironmentDialog = false"
            />
          </div>
        </v-card-title>
        <v-card-text>
          <p class="mb-4">
            Program Access will also be copied onto the selected environment.
          </p>
          <v-alert
            v-if="availableEnvironments.length === 0"
            type="info"
            variant="tonal"
            class="mb-4"
            text="This group has already been added to all available environments"
          />
          <v-select
            v-else
            v-model="selectedEnvironment"
            :items="availableEnvironments"
            :disabled="addEnvironmentLoading || groupsLoading"
            item-title="name"
            item-value="id"
            label="Select Environment"
            outlined
            dense
          />
          <v-alert
            v-if="addEnvironmentError"
            key="v-alert-2"
            type="error"
            variant="tonal"
            class="mb-4"
            :text="addEnvironmentError"
          />
          <v-btn
            block
            color="primary"
            class="mt-4"
            :loading="addEnvironmentLoading"
            :disabled="!selectedEnvironment || groupsLoading || !!addEnvironmentError"
            @click="addEnvironment()"
          >
            Confirm
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script lang="ts">
import { Role, Group, GroupRelationshipType } from '@policyfly/protobuf/pfbingo'
import { storeToRefs } from 'pinia'
import { computed, defineComponent, ref, useTemplateRef, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import GroupMetadataField from '@/components/GroupMetadataField.vue'
import { useApiStore } from '@/stores/api'
import { useDomainStore } from '@/stores/domain'
import { useEnvironmentStore } from '@/stores/environment'
import { useGroupStore } from '@/stores/group'
import { useProgramStore } from '@/stores/program'

import { RouteNames } from '@/router'
import { prettyGroupRelationshipTypes, prettyRoles, programRolesForDomain } from '@/utils/program'

import type { Domain, Environment, ListProgramsForGroupsResponse_Program, ListUsersForGroupResponse_User, Program } from '@policyfly/protobuf/pfbingo'

export default defineComponent({
  name: 'Group',

  components: {
    GroupMetadataField,
  },

  setup () {
    const environmentStore = useEnvironmentStore()
    const domainStore = useDomainStore()
    const apiStore = useApiStore()
    const groupStore = useGroupStore()
    const programStore = useProgramStore()
    const route = useRoute()
    const router = useRouter()
    const groupId = computed(() => route.params.id as string)
    const metadataField = useTemplateRef<typeof GroupMetadataField>('metadataField')

    // api and local states for the group being edited
    const loading = ref(false)
    const loadedGroup = ref<Group | null>(null)
    const loadedEnvironment = ref<string | null>(null)
    const loadedDomain = ref<Domain | null>(null)
    const localGroup = ref<Group | null>(null)
    const addGroupUsers = ref<boolean>(false)

    // track the environment/domain for which the group is currently loaded
    watch(() => ({
      domain: domainStore.currentDomain,
      environment: environmentStore.currentEnvironment,
    }), async ({ domain, environment }, oldSelection) => {
      const environmentId = environment?.id ?? null
      const hasUnsavedChanges = !Group.equals(localGroup.value ?? undefined, loadedGroup.value ?? undefined)
      const selectionChanged = loadedEnvironment.value !== environmentId || loadedDomain.value !== domain
      // if changing environments/domains and there are unsaved changes, confirm before proceeding
      if (hasUnsavedChanges && oldSelection?.environment && selectionChanged) {
        const answer = window.confirm(
          `Changing the environment or domain will discard any unsaved changes to ${localGroup.value ? `"${localGroup.value.name}"` : 'this group'}.
\nDo you want to continue?`)
        if (!answer) {
          environmentStore.setCurrentEnvironment(oldSelection.environment)
          domainStore.setCurrentDomain(oldSelection.domain)
          return
        } else if (loadedGroup.value) {
          localGroup.value = Group.clone(loadedGroup.value)
        } else {
          localGroup.value = Group.create({})
        }
      }
      // no environment/domain change to act on (e.g. cancelled switch)
      if (!selectionChanged && hasUnsavedChanges) return
      // load localGroup for new environment/domain
      loadedEnvironment.value = environmentId
      loadedDomain.value = domain
      if (!environmentId) return
      // programs that can be associated with the group (new or existing) are scoped to the environment/domain
      await programStore.loadScopedPrograms()
      if (!groupId.value) {
        localGroup.value = Group.create({})
        return
      }
      loading.value = true

      const { group } = await apiStore.loadGroupDetails(groupId.value)
      const { groups } = await apiStore.listProgramsForGroup({
        groupIds: [group!.id],
        environmentId,
        domain,
      })
      await loadUsers(environmentId, domain)

      loadedGroup.value = group || null
      localGroup.value = loadedGroup.value ? Group.clone(loadedGroup.value) : null
      groupPrograms.value = groups.find((g) => g.groupId === group!.id)?.programs || []
      loading.value = false
    }, { immediate: true })

    // users
    const groupUsers = ref<ListUsersForGroupResponse_User[]>([])

    async function loadUsers (environmentId = environmentStore.currentEnvironment!.id, domain = domainStore.currentDomain): Promise<void> {
      if (!groupId.value) return
      const response = await apiStore.listUsersForGroup({
        groupId: groupId.value,
        environmentId,
        domain,
      })
      groupUsers.value = response.users || []
    }

    async function viewUser (userId: string): Promise<void> {
      await router.push({ name: RouteNames.USER_EDIT, params: { id: userId } })
    }

    // programs
    const selectedRelationship = ref<GroupRelationshipType | null>(null)
    const selectedGroupUserRole = ref<Role | null>(null)
    const programRelationships = [
      GroupRelationshipType.GROUP_RELATIONSHIP_TYPE_OWNER,
      GroupRelationshipType.GROUP_RELATIONSHIP_TYPE_DISTRIBUTION_PARTNER,
    ].map((type) => {
      return { type, name: prettyGroupRelationshipTypes.get(type) }
    })
    const availableGroupUserRoles = computed(() => {
      return programRolesForDomain(domainStore.currentDomain, selectedRelationship.value)
        .map((role) => ({ role, name: prettyRoles.get(role) }))
    })

    const { scopedPrograms, scopedProgramsLoading: programsLoading } = storeToRefs(programStore)
    const programItems = computed(() => {
      // filter out programs already associated with the group
      const associatedProgramIds = new Set(groupPrograms.value.map(({ program }) => program?.id))
      return scopedPrograms.value.filter((p) => !associatedProgramIds.has(p.id))
    })

    const groupPrograms = ref<ListProgramsForGroupsResponse_Program[]>([])
    const actionDisabled = computed<boolean>(() => {
      return loading.value || !localGroup.value?.name ||
        // validation for existing groups
        (!!localGroup.value.id && !metadataField.value?.valid)
    })
    async function createGroup (): Promise<void> {
      const group = localGroup.value
      const environmentId = environmentStore.currentEnvironment?.id
      if (!group || !group.name || !environmentId) return
      const answer = window.confirm(`Are you sure you want to create group "${group.name}"?`)
      if (!answer) return
      const domain = domainStore.currentDomain
      loading.value = true
      const response = await apiStore.createGroup({
        group,
      })
      await apiStore.addGroupToEnvironment({
        groupId: response.group!.id,
        environmentId,
        domain,
      })
      for (const { program, relationshipType } of groupPrograms.value) {
        await apiStore.addGroupToProgram({
          groupId: response.group!.id,
          programId: program!.id,
          relationshipType,
          environmentId,
          domain,
        })
      }
      localGroup.value = response.group!
      loading.value = false
      await router.push({ name: RouteNames.GROUP_EDIT, params: { id: response.group!.id } })
    }
    async function updateGroup (): Promise<void> {
      const group = localGroup.value
      if (!group || !group.id || !group.name) return
      loading.value = true
      const response = await apiStore.updateGroup({
        group: localGroup.value!,
      })
      localGroup.value = response.group!
      loading.value = false
    }

    const selectedPrograms = ref<Program[]>([])
    const groupUserProgramAdditions = computed(() => {
      const role = selectedGroupUserRole.value
      if (!role) return []
      return groupUsers.value.flatMap(({ user }) => {
        if (!user?.id) return []
        return [{
          userId: user.id,
          role,
        }]
      })
    })
    const grantGroupUsersUnavailable = computed(() => {
      return !localGroup.value?.id ||
        !groupUsers.value.some(({ user }) => !!user?.id) ||
        !selectedPrograms.value.length ||
        !selectedRelationship.value
    })
    watch(grantGroupUsersUnavailable, (unavailable) => {
      if (unavailable) addGroupUsers.value = false
    })
    watch(addGroupUsers, (shouldAddGroupUsers) => {
      if (!shouldAddGroupUsers) selectedGroupUserRole.value = null
    })
    watch(selectedRelationship, () => {
      selectedGroupUserRole.value = null
    })

    async function addPrograms (): Promise<void> {
      const relationshipType = selectedRelationship.value
      const programsToAdd = [...selectedPrograms.value]
      if (!selectedPrograms.value.length || !relationshipType) return
      // new groups stage program associations until the group is created
      if (!localGroup.value?.id) {
        for (const program of programsToAdd) {
          groupPrograms.value.push({
            program,
            relationshipType,
          })
        }
      } else {
        const domain = domainStore.currentDomain
        const environmentId = environmentStore.currentEnvironment!.id
        const currentGroupId = localGroup.value.id
        const shouldGrantGroupUsers = addGroupUsers.value && !!groupUserProgramAdditions.value.length
        loading.value = true
        try {
          for (const program of programsToAdd) {
            await apiStore.addGroupToProgram({
              groupId: currentGroupId,
              programId: program.id,
              relationshipType,
              environmentId,
              domain,
            })
            if (shouldGrantGroupUsers) {
              await apiStore.addToProgram({
                groupId: currentGroupId,
                programId: program.id,
                environmentId,
                additions: groupUserProgramAdditions.value,
                domain,
              })
            }
            groupPrograms.value.push({
              program,
              relationshipType,
            })
          }
        } catch (e) {
          alert('Failed when updating the Group, check console for the full error.')
          console.error('Error adding Programs/Users to Group:', e)
        } finally {
          loading.value = false
        }
      }
      selectedPrograms.value = []
      selectedRelationship.value = null
      selectedGroupUserRole.value = null
      addGroupUsers.value = false
    }

    async function removeProgram (program: Program | undefined): Promise<void> {
      if (!program) return
      if (groupId.value && !window.confirm(`Are you sure you want to remove "${program.name}" from group "${localGroup.value?.name}"?`)) return
      if (groupId.value) {
        const domain = domainStore.currentDomain
        loading.value = true
        await apiStore.removeGroupFromProgram({
          groupId: localGroup.value!.id,
          programId: program.id,
          environmentId: environmentStore.currentEnvironment!.id,
          domain,
        })
      }
      groupPrograms.value = groupPrograms.value.filter((p) => p.program?.id !== program.id)
      loading.value = false
    }

    const { environments } = storeToRefs(environmentStore)
    const { groupsByEnvironment, loading: groupsLoading } = storeToRefs(groupStore)
    const groupEnvironmentIds = computed(() => new Set(
      Object.entries(groupsByEnvironment.value)
        .filter(([, groups]) => groups.some((group) => group.id === localGroup.value?.id))
        .map(([environmentId]) => environmentId),
    ))
    const groupEnvironmentNames = computed(() => environments.value
      .filter((environment) => groupEnvironmentIds.value.has(environment.id))
      .map((environment) => environment.name),
    )
    const availableEnvironments = computed<Environment[]>(() => {
      return environments.value.filter((environment) => !groupEnvironmentIds.value.has(environment.id))
    })
    const addEnvironmentDialog = ref(false)
    const addEnvironmentLoading = ref(false)
    const addEnvironmentError = ref('')
    const selectedEnvironment = ref<string | null>(null)
    function openAddEnvironmentDialog (): void {
      selectedEnvironment.value = null
      addEnvironmentError.value = ''
      addEnvironmentDialog.value = true
    }
    async function addEnvironment (): Promise<void> {
      const environmentId = selectedEnvironment.value
      const group = loadedGroup.value
      if (!environmentId || !group?.id) return

      const domain = domainStore.currentDomain
      const environmentName = environments.value.find((environment) => environment.id === environmentId)?.name ?? 'the selected environment'
      addEnvironmentError.value = ''
      addEnvironmentLoading.value = true
      try {
        await apiStore.addGroupToEnvironment({
          groupId: group.id,
          environmentId,
          domain,
        })
      } catch (error) {
        addEnvironmentError.value = `Could not add this group to ${environmentName}. No program access was copied.`
        console.error('Error adding group to environment:', error)
        addEnvironmentLoading.value = false
        return
      }

      const addProgramResults = await Promise.allSettled(
        groupPrograms.value.map(async ({ program, relationshipType }) => {
          await apiStore.addGroupToProgram({
            relationshipType,
            environmentId,
            domain,
            groupId: group.id,
            programId: program!.id,
          })
        }),
      )
      const failedPrograms = addProgramResults.flatMap((result, index) => {
        if (result.status === 'fulfilled') return []
        console.error('Error copying group program access:', result.reason)
        return [groupPrograms.value[index]?.program?.name ?? 'unknown program']
      })

      await groupStore.loadGroupsByEnvironment()
      if (failedPrograms.length) {
        addEnvironmentError.value = `The group was added to ${environmentName}, but program access could not be copied to ${failedPrograms.join(', ')}.`
      } else {
        addEnvironmentDialog.value = false
        selectedEnvironment.value = null
      }
      addEnvironmentLoading.value = false
    }

    return {
      groupId,
      localGroup,
      // programs
      Role,
      prettyGroupRelationshipTypes,
      programRelationships,
      programsLoading,
      scopedPrograms,
      programItems,
      groupPrograms,
      groupUsers,
      selectedPrograms,
      selectedRelationship,
      selectedGroupUserRole,
      availableGroupUserRoles,
      grantGroupUsersUnavailable,
      loading,
      actionDisabled,
      addGroupUsers,
      createGroup,
      updateGroup,
      addPrograms,
      removeProgram,
      viewUser,
      // add group to environment
      openAddEnvironmentDialog,
      addEnvironmentDialog,
      addEnvironmentError,
      addEnvironmentLoading,
      availableEnvironments,
      groupEnvironmentNames,
      groupsLoading,
      selectedEnvironment,
      addEnvironment,
    }
  },
})
</script>
